import { createSlice } from "@reduxjs/toolkit";

import axiosex from "axios";
import { dispatch } from "../index";

const API_BASE = "https://api.pgzone88.com/189a1sf898f";

const toBooleanFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const resolveAdminId = (input) => {
  if (input === null || input === undefined) {
    return input;
  }
  if (typeof input === "object") {
    if (input.AdminID !== undefined) {
      return input.AdminID;
    }
    if (input.adminId !== undefined) {
      return input.adminId;
    }
    if (input.id !== undefined) {
      return input.id;
    }
  }
  return input;
};

const initialState = {
  status: null,
  setup: null,
  actionResult: null,
  loading: false,
  error: null,
};

const slice = createSlice({
  name: "adminTwoFactor",
  initialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.loading = false;
      state.error = action.payload || null;
    },
    getStatusSuccess(state, action) {
      state.loading = false;
      const payload = action.payload || null;
      state.status = payload;
    },
    provisionSuccess(state, action) {
      state.loading = false;
      state.setup = action.payload || null;
      if (state.status && action.payload?.adminId !== undefined) {
        state.status = {
          ...state.status,
          adminId: state.status.adminId ?? action.payload.adminId,
          secretProvisioned: true,
          twoFactorEnabled: false,
        };
      }
    },
    enableSuccess(state, action) {
      state.loading = false;
      state.actionResult = action.payload || null;
      if (!state.status && action.payload?.adminId !== undefined) {
        state.status = {
          adminId: action.payload.adminId,
          twoFactorEnabled: true,
          secretProvisioned: true,
          canManageTwoFactor: toBooleanFlag(action.payload.canManageTwoFactor),
        };
        return;
      }
      if (state.status) {
        state.status = {
          ...state.status,
          twoFactorEnabled: true,
          secretProvisioned: true,
        };
      }
    },
    disableSuccess(state, action) {
      state.loading = false;
      state.actionResult = action.payload || null;
      if (!state.status && action.payload?.adminId !== undefined) {
        state.status = {
          adminId: action.payload.adminId,
          twoFactorEnabled: false,
          secretProvisioned: toBooleanFlag(action.payload.secretProvisioned),
          canManageTwoFactor: toBooleanFlag(action.payload.canManageTwoFactor),
        };
        return;
      }
      if (state.status) {
        state.status = {
          ...state.status,
          twoFactorEnabled: false,
        };
      }
    },
    resetSuccess(state, action) {
      state.loading = false;
      state.setup = action.payload || null;
      if (state.status && action.payload?.adminId !== undefined) {
        state.status = {
          ...state.status,
          twoFactorEnabled: false,
          secretProvisioned: true,
        };
      }
    },
    manageAccessSuccess(state, action) {
      state.loading = false;
      state.actionResult = action.payload || null;
      const adminId = action.payload?.adminId;
      const canManageTwoFactor = toBooleanFlag(
        action.payload?.canManageTwoFactor,
      );
      if (state.status) {
        state.status = {
          ...state.status,
          adminId: state.status.adminId ?? adminId,
          canManageTwoFactor,
        };
      }
    },
    clearSetup(state) {
      state.setup = null;
    },
    clearActionResult(state) {
      state.actionResult = null;
    },
  },
});

export default slice.reducer;

const handleError = (error) => {
  const payload = error?.response?.data || error.message || error;
  dispatch(slice.actions.hasError(payload));
  throw payload;
};

const request = async (method, url, data) => {
  const config = {
    method,
    url,
    data,
    headers: {
      "Cache-Control": "no-store, no-cache, must-revalidate",
    },
  };
  const response = await axiosex(config);
  return response.data;
};

export function getAdminTwoFactorStatus(id) {
  return async () => {
    const adminId = resolveAdminId(id);
    if (adminId === undefined || adminId === null) {
      dispatch(slice.actions.getStatusSuccess(null));
      return null;
    }
    try {
      dispatch(slice.actions.startLoading());
      const data = await request("get", `${API_BASE}/twofactor/${adminId}`);
      dispatch(slice.actions.getStatusSuccess(data));
      return data;
    } catch (error) {
      return handleError(error);
    }
  };
}

export function provisionAdminTwoFactor(id, options = {}) {
  return async () => {
    const adminId = resolveAdminId(id);
    if (adminId === undefined || adminId === null) {
      throw new Error("Admin id is required to provision two-factor");
    }
    try {
      dispatch(slice.actions.startLoading());
      const body = options?.issuer ? { issuer: options.issuer } : {};
      const data = await request(
        "post",
        `${API_BASE}/twofactor/${adminId}/setup`,
        body,
      );
      const payload = { ...data, adminId };
      dispatch(slice.actions.provisionSuccess(payload));
      return payload;
    } catch (error) {
      return handleError(error);
    }
  };
}

export function enableAdminTwoFactor(id, code) {
  return async () => {
    const adminId = resolveAdminId(id);
    if (adminId === undefined || adminId === null) {
      throw new Error("Admin id is required to enable two-factor");
    }
    try {
      dispatch(slice.actions.startLoading());
      const data = await request(
        "post",
        `${API_BASE}/twofactor/${adminId}/enable`,
        {
          code,
        },
      );
      const payload = { ...data, adminId };
      dispatch(slice.actions.enableSuccess(payload));
      return payload;
    } catch (error) {
      return handleError(error);
    }
  };
}

export function disableAdminTwoFactor(id) {
  return async () => {
    const adminId = resolveAdminId(id);
    if (adminId === undefined || adminId === null) {
      throw new Error("Admin id is required to disable two-factor");
    }
    try {
      dispatch(slice.actions.startLoading());
      const data = await request(
        "post",
        `${API_BASE}/twofactor/${adminId}/disable`,
      );
      const payload = { ...data, adminId };
      dispatch(slice.actions.disableSuccess(payload));
      return payload;
    } catch (error) {
      return handleError(error);
    }
  };
}

export function resetAdminTwoFactor(id, options = {}) {
  return async () => {
    const adminId = resolveAdminId(id);
    if (adminId === undefined || adminId === null) {
      throw new Error("Admin id is required to reset two-factor");
    }
    try {
      dispatch(slice.actions.startLoading());
      const body = options?.issuer ? { issuer: options.issuer } : {};
      const data = await request(
        "post",
        `${API_BASE}/twofactor/${adminId}/reset`,
        body,
      );
      const payload = { ...data, adminId };
      dispatch(slice.actions.resetSuccess(payload));
      return payload;
    } catch (error) {
      return handleError(error);
    }
  };
}

export function setAdminTwoFactorManageAccess(id, canManageTwoFactor) {
  return async () => {
    const adminId = resolveAdminId(id);
    if (adminId === undefined || adminId === null) {
      throw new Error("Admin id is required to update two-factor access");
    }
    const body = {
      canManageTwoFactor: toBooleanFlag(canManageTwoFactor),
    };
    try {
      dispatch(slice.actions.startLoading());
      const data = await request(
        "put",
        `${API_BASE}/twofactor/manage-access/${adminId}`,
        body,
      );
      const payload = { ...data, adminId, ...body };
      dispatch(slice.actions.manageAccessSuccess(payload));
      return payload;
    } catch (error) {
      return handleError(error);
    }
  };
}

export const { clearSetup, clearActionResult } = slice.actions;
